import React, { useState, useEffect } from 'react';
import { 
  Crown, 
  Check, 
  Sparkles, 
  X, 
  ShieldCheck, 
  AlertCircle
} from 'lucide-react';
import { UserProfile, PraxisPaper, TierType } from '../types';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { getPaperCatalogSummaries, isUserAdmin } from '../lib/firebase';

interface PricingModalProps {
  userProfile: UserProfile;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
  onClose: () => void;
}

interface PricingPlan {
  id: 'basic' | 'standard' | 'extended';
  name: string;
  durationMonths: number;
  durationLabel: string;
  totalPrice: number;
  effectiveMonthlyRate: number;
  billingText: string;
  badge?: string;
  savings?: string;
  isPopular?: boolean;
}

const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'basic',
    name: 'Basic',
    durationMonths: 1,
    durationLabel: '1 Month',
    totalPrice: 35,
    effectiveMonthlyRate: 35,
    billingText: '$35 billed monthly',
    badge: '1 Month Access'
  },
  {
    id: 'standard',
    name: 'Standard',
    durationMonths: 2,
    durationLabel: '2 Months',
    totalPrice: 60,
    effectiveMonthlyRate: 30,
    billingText: '$30/mo, billed $60 every 2 months',
    badge: 'Most Popular',
    savings: 'Save 14%',
    isPopular: true
  },
  {
    id: 'extended',
    name: 'Extended',
    durationMonths: 4,
    durationLabel: '4 Months',
    totalPrice: 112,
    effectiveMonthlyRate: 28,
    billingText: '$28/mo, billed $112 every 4 months',
    badge: 'Best Value',
    savings: 'Save 20%'
  }
];

export const PricingModal: React.FC<PricingModalProps> = ({
  userProfile,
  onClose
}) => {
  const [papers, setPapers] = useState<PraxisPaper[]>(PRAXIS_PAPERS);
  const [loadingPapers, setLoadingPapers] = useState<boolean>(true);
  const [selectedPlanId, setSelectedPlanId] = useState<'basic' | 'standard' | 'extended'>('standard');

  // Paystack Initialization State
  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    async function loadLivePapers() {
      try {
        const livePapers = await getPaperCatalogSummaries();
        if (isMounted && livePapers && livePapers.length > 0) {
          const sorted = [...livePapers].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
          setPapers(sorted);
        }
      } catch (err) {
        console.warn('[PricingModal] Could not fetch live papers from Firestore, using static list:', err);
      } finally {
        if (isMounted) setLoadingPapers(false);
      }
    }
    loadLivePapers();
    return () => { isMounted = false; };
  }, []);

  const selectedPlan = PRICING_PLANS.find(p => p.id === selectedPlanId) || PRICING_PLANS[1];

  const handleInitializePaystackCheckout = async () => {
    setIsInitializing(true);
    setErrorMsg(null);

    const allPaperIds = papers.map(p => p.code);

    try {
      const response = await fetch('/api/payments/initialize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userProfile.email || 'candidate@praxisprep.edu',
          uid: userProfile.uid,
          selectedTier: selectedPlan.id,
          planDurationMonths: selectedPlan.durationMonths,
          planPriceUsd: selectedPlan.totalPrice,
          selectedPapers: allPaperIds
        })
      });

      const data = await response.json();

      if (!response.ok || !data.authorization_url) {
        console.error('[PricingModal Paystack Error]:', response.status, data);
        setErrorMsg(data.error || 'Failed to initialize Paystack checkout. Please try again.');
        setIsInitializing(false);
        return;
      }

      // Navigate browser directly to Paystack hosted checkout page
      window.location.href = data.authorization_url;
    } catch (err: any) {
      console.error('[PricingModal Paystack Exception]:', err);
      setErrorMsg(`Connection Error: Could not reach payment server (${err?.message || 'Network error'})`);
      setIsInitializing(false);
    }
  };

  const featureList = [
    'Full access to every exam family and subject (state exams + Praxis Series)',
    'All practice modes: Study, Timed, Rapid Fire, Adaptive',
    'Mock Exam Series — timed, domain-accurate, full-length simulations',
    'AI-scored constructed-response and essay feedback with rubric breakdown',
    '24/7 live chat + WhatsApp support',
    'One active device per session'
  ];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 relative overflow-hidden">
        {/* Anchored Header */}
        <div className="px-6 pt-5 pb-3 sm:px-8 sm:pt-6 sm:pb-3 border-b border-slate-100 relative shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-2 rounded-full hover:bg-slate-100 transition cursor-pointer"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center space-y-1.5 max-w-xl mx-auto">
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[11px] font-extrabold bg-indigo-500/10 text-indigo-700 border border-indigo-500/30">
              <Crown className="w-3.5 h-3.5 text-indigo-600" /> All-Access Educator Subscription
            </div>

            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
              Choose Your All-Access Plan
            </h2>

            <p className="text-xs text-slate-600 leading-relaxed">
              Every plan includes complete access to all exam papers, standardized mock series, AI rubric scoring, and 24/7 live support.
            </p>

            {isUserAdmin(userProfile) && (
              <div className="mt-2 p-2.5 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs font-semibold flex items-center justify-center gap-2">
                <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0" />
                <span>Admin Account Detected: You already have 100% full VIP access.</span>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="px-6 py-4 sm:px-8 sm:py-5 overflow-y-auto flex-1 space-y-4">
          {errorMsg && (
            <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">Checkout Initialization Error</p>
                <p className="mt-0.5">{errorMsg}</p>
              </div>
            </div>
          )}

          {/* Competitor Callout Banner */}
          <div className="bg-indigo-50/80 border border-indigo-200/80 rounded-xl py-2 px-3.5 text-center text-xs font-medium text-indigo-900 flex items-center justify-center gap-2 shadow-2xs">
            <Sparkles className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
            <span>
              Other test prep platforms charge up to $50/month — per exam. PrePraxis gives you every exam family for one price.
            </span>
          </div>

          {/* 3 Selectable Pricing Tier Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 pt-1.5">
            {PRICING_PLANS.map((plan) => {
              const isSelected = selectedPlanId === plan.id;
              return (
                <div
                  key={plan.id}
                  onClick={() => setSelectedPlanId(plan.id)}
                  className={`relative rounded-2xl pt-6 pb-4 px-4 cursor-pointer transition-all duration-200 flex flex-col justify-between border-2 ${
                    isSelected
                      ? 'border-indigo-600 bg-indigo-50/50 shadow-md ring-2 ring-indigo-500/20'
                      : 'border-slate-200 bg-white hover:border-indigo-300 hover:bg-slate-50/60 shadow-xs'
                  }`}
                >
                  {/* Badges */}
                  {plan.isPopular ? (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-600 text-white border border-indigo-500 ring-2 ring-white shadow-xs">
                        MOST POPULAR • SAVE 14%
                      </span>
                    </div>
                  ) : plan.id === 'extended' ? (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-600 text-white border border-emerald-500 ring-2 ring-white shadow-xs">
                        BEST VALUE • SAVE 20%
                      </span>
                    </div>
                  ) : null}

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-extrabold text-slate-800 tracking-tight">
                        {plan.name}
                      </span>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300'
                      }`}>
                        {isSelected && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl sm:text-3xl font-black text-slate-900">
                          ${plan.effectiveMonthlyRate}
                        </span>
                        <span className="text-xs text-slate-500 font-semibold">/mo</span>
                      </div>

                      <p className="text-[11px] font-semibold text-indigo-700 bg-indigo-100/70 px-2 py-0.5 rounded-md inline-block">
                        {plan.id === 'basic' && '$35/mo (1 month)'}
                        {plan.id === 'standard' && '$30/mo (billed $60/2 months)'}
                        {plan.id === 'extended' && '$28/mo (billed $112/4 months)'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 pt-2.5 border-t border-slate-200/80 text-[11px] text-slate-500 font-medium flex items-center justify-between">
                    <span>Duration:</span>
                    <span className="font-bold text-slate-700">{plan.durationLabel}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Consolidated Single Unified Feature Block Directly Below Pricing Cards */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 space-y-2.5">
            <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
              Included in every plan:
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {featureList.map((feat, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <div className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 stroke-[2.5]" />
                  </div>
                  <span className="text-slate-700 font-medium leading-tight">{feat}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Anchored Bottom Checkout Action */}
        <div className="px-6 py-4 sm:px-8 sm:py-4 bg-slate-50/90 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>256-bit Encrypted Paystack Hosted Checkout</span>
          </div>

          <button
            onClick={handleInitializePaystackCheckout}
            disabled={isInitializing}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-sm px-6 py-3 rounded-xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {isInitializing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Connecting to Paystack...
              </>
            ) : (
              <>
                Subscribe with Paystack (${selectedPlan.totalPrice} for {selectedPlan.durationLabel})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
